import {
  EventStatus,
  EventType,
} from "@ticketingplatform/api/dist/__generated__/globalTypes";
import { TabsProps } from "antd";
import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import TabLabelField from "components/fields/TabLabelField";
import { namePathOrMatcher } from "utils/formUtils";

import DiscountCodes from "./Tabs/DiscountCodes";
import EventInfo from "./Tabs/EventInfo";
import Tickets from "./Tabs/Tickets";
import { UseEventFormSubmitButtons } from "./types";
import { EVENT_TAB_NAMES } from "./consts";

export const useEventFormSubmitButtons: UseEventFormSubmitButtons = (form) => ({
  onPublish: useCallback(() => {
    form?.setFieldValue(["input", "status"], EventStatus.PUBLISHED);
  }, [form]),
  onDraft: useCallback(() => {
    form?.setFieldValue(["input", "status"], EventStatus.DRAFT);
  }, [form]),
});

export const useEventFormTabs = (eventType: EventType): TabsProps["items"] => {
  const intl = useIntl();

  return useMemo<TabsProps["items"]>(
    () => [
      {
        disabled: false,
        label: (
          <TabLabelField
            label={intl.formatMessage({
              defaultMessage: "Event Info",
            })}
            matcher={namePathOrMatcher({
              equals: [
                ["input", "name"],
                ["input", "description"],
                ["input", "ticketDescription"],
                ["input", "timezone"],
                ["input", "date"],
                ["input", "doorTime"],
                ["input", "ageLimit"],
                ["input", "recurringSettings"],
                ["input", "publishAt"],
                ["input", "sendTicketsAt"],
                ["input", "youtubeUrl"],
                ["input", "image"],
                ["input", "seatMap"],
                ["input", "presalePasswords"],
              ],
            })}
          />
        ),
        key: EVENT_TAB_NAMES.details,
        children: <EventInfo />,
        forceRender: true,
      },
      {
        disabled: false,
        label: (
          <TabLabelField
            label={
              eventType === EventType.NONTICKETED
                ? intl.formatMessage({
                    defaultMessage: "Sign up options",
                  })
                : intl.formatMessage({
                    defaultMessage: "Tickets",
                  })
            }
            matcher={namePathOrMatcher({
              startsWith: [["input", "ticketPools"]],
            })}
          />
        ),
        key: EVENT_TAB_NAMES.tickets,
        children: <Tickets {...{ eventType }} />,
        forceRender: true,
      },
      ...(eventType === EventType.TICKETED
        ? [
            {
              disabled: false,
              label: (
                <TabLabelField
                  label={intl.formatMessage({
                    defaultMessage: "Discount Codes",
                  })}
                  matcher={namePathOrMatcher({
                    startsWith: [["input", "discountCodes"]],
                  })}
                />
              ),
              key: EVENT_TAB_NAMES.codes,
              children: <DiscountCodes />,
              forceRender: true,
            },
          ]
        : []),
    ],
    [intl, eventType]
  );
};
